import { FaPencil, FaTrash } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { css } from "../../../../../styled-system/css";
import { Flex, Grid, HStack, VStack } from "../../../../../styled-system/jsx";
import { Button } from "../../../../components/core/basic/Button";
import { HoverInfo } from "../../../../components/core/basic/Tooltip";
import AssetSummary from "../../../../components/core/blocks/AssetSelector/AssetSummary";
import ConfirmationModal from "../../../../components/core/blocks/ConfirmationModal";
import { PaginationWidget } from "../../../../components/table/Pagination";
import { TextField } from "../../../../components/fields/base/TextField";
import usePagination from "../../../../hooks/usePagination";

type SpotlightVideo = {
  assetId: string;
  assetVersion?: string;
  headline: string;
  description?: string;
  videoUrl?: string;
  productFeatures?: string[];
  optimized?: boolean;
  adGroupIds?: string[];
};

type Props = {
  videos: SpotlightVideo[];
  onEdit: (assetId: string) => void;
  onDelete: (assetId: string) => void;
};

const fullWidthField = css.raw({ width: "100%" });

const PAGE_SIZE = 3;

export const SpotlightVideoSummary = ({ videos, onEdit, onDelete }: Props) => {
  const isLastVideo = videos.length === 1;
  const { pageData, page, lastPage, setPage } = usePagination(videos, PAGE_SIZE);
  const { t } = useTranslation(["advertising"]);

  return (
    <VStack alignItems="flex-start" width="100%" gap="medium">
      {lastPage > 1 && (
        <HStack width="100%" justifyContent="flex-end">
          <PaginationWidget
            currentPage={page}
            setCurrentPage={setPage}
            pageSize={PAGE_SIZE}
            setPageSize={() => {}}
            totalCount={videos.length}
            totalPages={lastPage}
            canPaginateBackward={page > 0}
            canPaginateForward={page + 1 < lastPage}
            canSetPageSize={false}
            currentPageRowCount={pageData.length}
            pageSizeLabel={(size) => t(($) => $.advertising["{size} videos"], { size })}
          />
        </HStack>
      )}
      <Grid width="100%" gap="medium" gridTemplateColumns={{ base: "1fr", xl: "repeat(3, 1fr)" }}>
        {pageData.map((video, index) => {
          const displayIndex = page * PAGE_SIZE + index + 1;
          const { assetId, headline, description } = video;
          return (
            <VStack
              key={assetId}
              css={{ width: "100%", gap: "small", border: "1px solid {colors.gray.200}", padding: "medium" }}
            >
              <Flex justifyContent={"space-between"} width={"100%"}>
                <span className={css({ whiteSpace: "nowrap", fontWeight: 500 })}>
                  {t(($) => $.advertising["Video #{number}"], { number: displayIndex })}
                </span>
                <HStack justifyContent="flex-end" width="100%" gap="large">
                  <Button
                    variant="contentOnly"
                    onPress={() => onEdit(assetId)}
                    tooltip={t(($) => $.advertising["Update Spotlight Video"])}
                  >
                    <FaPencil />
                  </Button>
                  {isLastVideo ?
                    <HoverInfo icon={<FaTrash />} iconCss={{ color: "gray.300", cursor: "default" }}>
                      {t(
                        ($) =>
                          $.advertising[
                            "At least one video is required. Use the pause toggle to disable the extension."
                          ],
                      )}
                    </HoverInfo>
                  : <ConfirmationModal
                      title={t(($) => $.advertising["Remove Spotlight Video"])}
                      confirmationMessage={t(
                        ($) => $.advertising["Are you sure you want to remove Spotlight Video #{number}?"],
                        {
                          number: displayIndex,
                        },
                      )}
                      confirmButtonText={t(($) => $.advertising["Remove"])}
                      onConfirm={() => onDelete(assetId)}
                      modalButtonText={<FaTrash />}
                      modalButtonProps={{
                        variant: "contentOnly",
                        tooltip: t(($) => $.advertising["Remove Spotlight Video"]),
                      }}
                    />
                  }
                </HStack>
              </Flex>
              <AssetSummary
                assetId={assetId}
                assetDescriptor={""}
                assetType="VIDEO"
                hidden={false}
                summaryContainerClasses=""
                displayAssetName={false}
                columnSpan={12}
              />
              <VStack gap="xsmall" width="100%">
                <TextField
                  label={t(($) => $.advertising["Asset ID"])}
                  value={assetId}
                  isDisabled
                  css={fullWidthField}
                />
                <TextField
                  label={t(($) => $.advertising["Headline"])}
                  value={headline}
                  isDisabled
                  css={fullWidthField}
                />
                <TextField
                  label={t(($) => $.advertising["Description"])}
                  value={description ?? ""}
                  isDisabled
                  css={fullWidthField}
                />
              </VStack>
            </VStack>
          );
        })}
      </Grid>
    </VStack>
  );
};

export type { SpotlightVideo };
